/**
 * React Query wrappers over `./api`.
 *
 * The one non-obvious thing here is polling. Neither mobile client refreshes C1 at all — no
 * pull-to-refresh, no SSE, no timer; freshness comes from the screen being recreated on every
 * entry (`LaunchedEffect(Unit)`, `.task`). A browser tab has no such moment: it can sit open for
 * an hour showing «Ищем оператора» while the tanker is already on its way, and a stale status on
 * the one screen that says «somebody is driving to you» is worse than showing nothing.
 *
 * So this hook polls — but only while something is actually running, and only in the foreground:
 * an idle account with an empty list costs zero requests, which is what keeps the poll cheap
 * enough to justify at all. Refetch on focus and on reconnect are NOT set here; they are already
 * defaults in `createQueryClient` and repeating them locally is how the two drift apart.
 */
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/app/query';
import { fetchActiveOrders } from '@/entities/order/api';

/**
 * 25 s — the middle of the 20–30 s band agreed for wave 2. Fast enough that «оператор назначен»
 * → «оператор едет к вам» lands while the person is still looking at the card, slow enough that
 * an hour of tracking is ~144 requests rather than a load test. D1 replaces this with SSE.
 */
export const ACTIVE_ORDERS_POLL_MS = 25_000;

/**
 * Every выезд still running on this account, newest first.
 *
 * Returns the whole array — an active order belongs to a CAR, so an account with three cars can
 * have three of them at once. Callers must render a list; `data[0]` is not «the» active order.
 *
 * Note what is deliberately absent: no `catch` turning a failure into `[]`. The error state has
 * to reach the screen, because «нет активных заявок» and «не смогли спросить» look identical to
 * a rider and mean opposite things (audit 2026-08-17).
 */
export function useActiveOrders() {
  return useQuery({
    queryKey: queryKeys.activeOrders,
    queryFn: fetchActiveOrders,
    // An empty list needs no timer; a non-empty one is a выезд in progress. The predicate reads
    // the cached array rather than a piece of component state so a background tab that comes
    // back with a now-empty list also stops polling.
    refetchInterval: (query) =>
      (query.state.data?.length ?? 0) > 0 ? ACTIVE_ORDERS_POLL_MS : false,
    refetchIntervalInBackground: false,
    // Shorter than the client-wide 30 s: returning to the tab mid-выезд should re-ask, and with
    // the default the first 30 s after a focus event would silently serve the cached status.
    staleTime: 10_000,
  });
}
